import { useState } from "react";

import { PharmacyOrdersStatusView } from "@/Common/Components/PharmacyOrders/PharmacyOrdersStatusView";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { Guid } from "@/Common/Models/Guid";
import { PharmacyOrderMedicationStatus, PharmacyOrderStatus, PrescriptionRenewalsViewRenewMedicationTableOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IncreasedOrDecreasedMedicationText } from "@/Patient/Components/Medication/IncreasedOrDecreasedMedicationText";
import { LongTermMedicationIcon } from "@/Patient/Components/Medication/LongTermMedicationIcon";
import { MedicationHelper } from "@/Patient/Helpers/MedicationHelper";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { EnumPrescriptionRenewalMedicationsStatus } from "../Models/EnumPrescriptionRenewalMedicationsStatus";
import { PrescriptionRenewalMedications } from "../Models/PrescriptionRenewalMedications";

import styles from "./PrescriptionRenewalsViewRenewMedicationTable.module.scss";

interface IPrescriptionRenewalsViewRenewMedicationTableProps {
    medicationModel: MedicationModel[];
    relatedPrescriptionRenewalMedications?: PrescriptionRenewalMedications[];
    shouldDisableAction?: boolean;
    shouldDisableEvent?: boolean;
    hasActionColumn?: boolean;
    hasStatusColumn?: boolean;
    defaultOrderDirection?: SortDirection;
    onRemoveMedication?: (medication: MedicationModel) => void;
    onSortMedications: (sortedMedications: MedicationModel[]) => void;
    onDenyClick?: (medicationId: Guid) => void;
    onEditMedicationPopupOpen?: (medicationModelId: string) => void;
    onDropHandlerCallback?: (newMedicationModel: MedicationModel[]) => void;
}

const tableColumnName = {
    Status: "Status",
    Medication: "Medication",
    End: "End",
    Dosage: "Dosage",
    Quantity: "Quantity",
    Refills: "Refills",
    Duration: "Duration",
    Action: "Action"
} as const;

const getTableRenderOptions = (columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions => {
    const defaultTablePluginOptions = {
        MinColWidth: 100,
        MinTableWidth: 950,
        Cols: columnOptions.filter(p => p.IsHidden === false).map(p => p.WidthPercentage).toArray(),
        FirstColumnNoDrCOLUMNS_OPTIONSag: true,
        LastColumnNoDrag: true,
        Autoresize: true,
        OnColumnWidthChange: null
    };

    return Immutable.fromJS(defaultTablePluginOptions);
};

export function PrescriptionRenewalsViewRenewMedicationTable(props: IPrescriptionRenewalsViewRenewMedicationTableProps): JSX.Element {
    const { getText } = useTranslation();

    const [queryOrderType, setQueryOrderType] = useState<number>(props.hasStatusColumn ?
        PrescriptionRenewalsViewRenewMedicationTableOrderType.Status : PrescriptionRenewalsViewRenewMedicationTableOrderType.Medication
    );
    const [queryOrderDirection, setQueryOrderDirection] = useState<number>(props.defaultOrderDirection ?? SortDirection.ASC);
    const [rowBeingDragged, setRowBeingDragged] = useState<MedicationModel>(null);
    const tableColumnOptions = getTableColumnOptions();
    const disabledLink = props.shouldDisableAction ? styles.disabledLink : "";

    function getTableColumnOptions(): Immutable.List<MyleTableColumnOptions> {
        function sortMedicationsBy(orderType: PrescriptionRenewalsViewRenewMedicationTableOrderType, sortDirection: SortDirection): void {
            const sortedMedications: MedicationModel[] = props.medicationModel.map((p: MedicationModel) => p.Clone(false, false, true));
            const isDescending: boolean = sortDirection === SortDirection.DESC;

            switch (orderType) {
                case PrescriptionRenewalsViewRenewMedicationTableOrderType.Medication:
                    sortedMedications.sortByProperty(p => p.GetName(), isDescending);
                    break;
                case PrescriptionRenewalsViewRenewMedicationTableOrderType.End:
                    sortedMedications.sortByProperty(p => p.EffectiveEndDate(), isDescending);
                    break;
                case PrescriptionRenewalsViewRenewMedicationTableOrderType.Refills:
                    sortedMedications.sortByProperty(p => p.Model.NumberOfRefill, isDescending);
                    break;
                case PrescriptionRenewalsViewRenewMedicationTableOrderType.Duration:
                    sortedMedications.sortByProperty(p => p.Model.TotalDurationManual, isDescending);
                    break;
                case PrescriptionRenewalsViewRenewMedicationTableOrderType.Status:
                    sortedMedications.sortByProperty(p => p.Model.pharmacyOrderMedicationStatus ?? Number.MIN_SAFE_INTEGER, isDescending);
                    break;
            }

            if (!isArrayNullOrUndefinedOrEmpty(sortedMedications)) {
                props.onSortMedications(sortedMedications);
            }
        }

        function tableSort(newOrderType: PrescriptionRenewalsViewRenewMedicationTableOrderType): void {
            const isOrderTypeDifferent = queryOrderType !== newOrderType;
            if (isOrderTypeDifferent) {
                setQueryOrderType(newOrderType);
            }

            const newSortDirection = MyleTableHelper.getNewSortDir(newOrderType, queryOrderType, queryOrderDirection);
            const isOrderDirectionDifferent = queryOrderDirection !== newSortDirection;
            if (isOrderDirectionDifferent) {
                setQueryOrderDirection(newSortDirection);
            }

            if (isOrderTypeDifferent || isOrderDirectionDifferent) {
                sortMedicationsBy(newOrderType, newSortDirection);
            }
        }

        function getCommonTableColumnOptions(
            headerText: string,
            fieldName: string,
            sortColumn: PrescriptionRenewalsViewRenewMedicationTableOrderType,
            WidthPercentage: number,
            isSortable: boolean = true
        ): MyleTableColumnOptions {
            return new MyleTableColumnOptions({
                HeaderText: headerText,
                FieldName: fieldName,
                RowClickEnabled: false,
                IsSortable: isSortable,
                SortColumn: sortColumn,
                SortDirection: queryOrderDirection,
                IsSortActive: queryOrderType === sortColumn,
                WidthPercentageDefault: WidthPercentage,
                WidthPercentage: WidthPercentage,
                sortingMethod: () => tableSort(sortColumn)
            });
        }

        let list = [
            getCommonTableColumnOptions(getText("MedicationLabel"), tableColumnName.Medication, PrescriptionRenewalsViewRenewMedicationTableOrderType.Medication, 20),
            getCommonTableColumnOptions(getText("EndFieldLabel"), tableColumnName.End, PrescriptionRenewalsViewRenewMedicationTableOrderType.End, 14),
            getCommonTableColumnOptions(getText("DosageLabel"), tableColumnName.Dosage, PrescriptionRenewalsViewRenewMedicationTableOrderType.Dosage, 14, false),
            getCommonTableColumnOptions(getText("QuantityLabel"), tableColumnName.Quantity, PrescriptionRenewalsViewRenewMedicationTableOrderType.Quantity, 14, false),
            getCommonTableColumnOptions(getText("RefillsLabel"), tableColumnName.Refills, PrescriptionRenewalsViewRenewMedicationTableOrderType.Refills, 12),
            getCommonTableColumnOptions(getText("DurationLabel"), tableColumnName.Duration, PrescriptionRenewalsViewRenewMedicationTableOrderType.Duration, 14)
        ];

        if (props.hasActionColumn) {
            list = [
                ...list,
                getCommonTableColumnOptions(getText("ActionLabel"), tableColumnName.Action, PrescriptionRenewalsViewRenewMedicationTableOrderType.Action, 12, false)
            ];
        }

        if (props.hasStatusColumn) {
            list = [
                getCommonTableColumnOptions(getText("StatusLabel"), tableColumnName.Status, PrescriptionRenewalsViewRenewMedicationTableOrderType.Status, 8),
                ...list
            ];
        }

        return Immutable.List(list);
    }

    function getTableColumns(): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<MedicationModel>>(
            tableColumnOptions,
            column => {
                if (column.FieldName == tableColumnName.Action)
                    column.DataCy = "prescription-renewal-table-action-header";

                return {
                    renderCell: item => {
                        const medicationModel = item.Model;

                        if (MedicationHelper.isMeidcationReplaced(medicationModel?.PrescriptionTab_OriginalMedication?.Model?.PrescriptionTab_PendingStatus))
                            return <div>{formatMedicationCellText(column.FieldName, item, false)}</div>;

                        if (MedicationHelper.isMedicationInterrupted(medicationModel.Model?.PrescriptionTab_PendingStatus))
                            return <div>{formatInterruptedMedicationCellText(column.FieldName, item)}</div>;

                        return <div>{formatMedicationCellText(column.FieldName, item)}</div>;
                    }
                };
            },
            false
        );

        function formatMedicationCellText(fieldName: string, item: TableDataSourceItem<MedicationModel>, isRenewed: boolean = true): string | JSX.Element {
            const medicationModel = item.Model;

            switch (fieldName) {
                case tableColumnName.Medication:
                    return (
                        <>
                            <div className={styles.medicationNameContainer} data-cy="prescriptionRenewal-renewMedication-medicationName">
                                {cacheStore.ClinicSettings.EnableLongTermMedication && medicationModel.Model.IsLongTerm && (
                                    <LongTermMedicationIcon
                                        className={styles.restoreHover}
                                        dataCy="prescriptionRenewal-renewMedication-longTermIcon"
                                    />
                                )}
                                <b>{item.Model.GetName()}</b>
                            </div>
                            {!isRenewed && (
                                <div className={styles.flexContainer}>
                                    <IncreasedOrDecreasedMedicationText
                                        medicationModel={medicationModel}
                                    />
                                </div>
                            )}
                            {isRenewed && !!medicationModel.Model?.Instruction && (
                                <div className={styles.flexContainer}>
                                    <span title={medicationModel.Model.Instruction} className={styles.instructionText}>
                                        {medicationModel.Model.Instruction}
                                    </span>
                                </div>
                            )}
                        </>
                    );
                case tableColumnName.End:
                    const endDate = item.Model.EffectiveEndDate() ? LuxonDateTimeParser.parse(item.Model.EffectiveEndDate()).toFormat("yyyy-MM-dd") : "";
                    return endDate;
                case tableColumnName.Dosage:
                    return renderDosages(item.Model);
                case tableColumnName.Quantity:
                    return renderQuantity(item.Model);
                case tableColumnName.Refills:
                    if (item.Model.IsNarcotic() || item.Model.Model?.Dosages.count() - 1) {
                        return "NR";
                    }
                    return item.Model.Model?.NumberOfRefill?.toString() ?? "";
                case tableColumnName.Duration:
                    return item.Model?.TicksToDays(item.Model?.Model?.TotalDurationManual).toString() ?? "";
                case tableColumnName.Action:
                    return renderAction(item.Model);
                case tableColumnName.Status:
                    return <div className="center">{renderStatus(item.Model?.Model?.pharmacyOrderMedicationStatus)}</div>;
            }
        }

        function formatInterruptedMedicationCellText(fieldName: string, item: TableDataSourceItem<MedicationModel>): string | JSX.Element {
            const medicationModel = item.Model;

            switch (fieldName) {
                case tableColumnName.Medication:
                    return (
                        <>
                            <div className={styles.medicationNameContainer}>
                                {cacheStore.ClinicSettings.EnableLongTermMedication && item.Model.Model.IsLongTerm && (
                                    <LongTermMedicationIcon className={styles.restoreHover} />
                                )}
                                <SpanControl
                                    className={styles.interruptedMedication}
                                    id={"status_" + medicationModel.Model.Id}
                                    text={MedicationModel.PrescriptionStatusIntToValue(Number(medicationModel.Model.PrescriptionTab_PendingStatus)) + " - " + medicationModel.GetName()}
                                />
                            </div>
                            <div>
                                <span>
                                    {getText("ReasonLabel") + ": "}
                                    <SpanControl
                                        id={"reason_" + medicationModel.Model.Id}
                                        text={medicationModel.getReason()}
                                    />
                                </span>
                            </div>
                        </>
                    );
                case tableColumnName.End:
                case tableColumnName.Dosage:
                case tableColumnName.Quantity:
                case tableColumnName.Refills:
                case tableColumnName.Duration:
                    return null;
                case tableColumnName.Action:
                    return renderAction(item.Model);
            }
        }

        function renderDosages(medicationModel: MedicationModel): JSX.Element {
            const dosagesToDisplay: string[] = medicationModel.GetDosagesDisplay({ includeRenewalsOnLastDosage: false });

            return (
                <>
                    {dosagesToDisplay.map((p: string, idx: number) => (
                        <div key={p + idx}>
                            {p}
                        </div>
                    ))}
                </>
            );
        }

        function renderQuantity(medicationModel: MedicationModel): JSX.Element {
            const quantityToDisplay: string[] = medicationModel.getMedicationQuantities();

            return (
                <>
                    {quantityToDisplay.map((p: string, idx: number) => (
                        <div key={p + idx}>
                            {p}
                        </div>
                    ))}
                </>
            );
        }

        function renderAction(medication: MedicationModel): JSX.Element {
            const medicationId = medication.Model.Id;
            const currentRelatedMedication: PrescriptionRenewalMedications = props.relatedPrescriptionRenewalMedications?.find(p => p.medicationId === medicationId);
            const currentMedication: MedicationModel = props.medicationModel?.find(p => p.Model.Id === medicationId) ?? null;
            const isMedicationInterrupted = MedicationHelper.isMedicationInterrupted(
                medication?.Model?.PrescriptionTab_PendingStatus
            );
            const isMedicationInterruptedOrPrescriptionRenewalCompleted: boolean = isMedicationInterrupted || props.shouldDisableAction;
            const isPrescriptionRenewalDenied = currentRelatedMedication?.status === EnumPrescriptionRenewalMedicationsStatus.Denied;
            const isPrescriptionRenewalCompletedAndApproved = props.shouldDisableAction && !isPrescriptionRenewalDenied;
            const isPrescriptionRenewalRenewedWithActions = IsNullOrUndefined(currentMedication?.Model?.PrescriptionStatusInt) || !MedicationHelper.isMedicationRenewed(String(currentMedication.Model.PrescriptionStatusInt));

            return (
                <div className={styles.actionContainer}>
                    {props.onDenyClick && (
                        <a
                            onClick={() => props.onDenyClick(medicationId)}
                            className={`${isPrescriptionRenewalDenied ? styles.selected : ""} ${styles.textActionLink} ${isPrescriptionRenewalCompletedAndApproved || isPrescriptionRenewalRenewedWithActions ? styles.disabledLink : ""} push-right-5`}
                        >
                            {getText("DenyLabel")}
                        </a>
                    )}
                    <div className={styles.linkContainer}>
                        {props.onEditMedicationPopupOpen && (
                            <a
                                onClick={(isMedicationInterruptedOrPrescriptionRenewalCompleted) ? null :
                                    () => props.onEditMedicationPopupOpen(medication.Model.Id)
                                }
                                className={`push-right-10 ${styles.actionLink} ${(isMedicationInterruptedOrPrescriptionRenewalCompleted) ? styles.disabledLink : ""}`}
                                data-cy="prescriptionRenewals-renewMedication-editButton"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M15.5 3.99106L12.0089 0.499923C11.8928 0.383845 11.755 0.291766 11.6034 0.228944C11.4517 0.166123 11.2892 0.133789 11.125 0.133789C10.9609 0.133789 10.7983 0.166123 10.6467 0.228944C10.495 0.291766 10.3572 0.383845 10.2411 0.499923L8.18319 2.55788L8.18307 2.55799L8.18296 2.55811L0.866177 9.87488C0.749723 9.99068 0.657397 10.1284 0.594544 10.2802C0.53169 10.4319 0.499557 10.5946 0.500005 10.7588V14.2499C0.500384 14.5813 0.632201 14.8991 0.86654 15.1334C1.10088 15.3677 1.4186 15.4995 1.75 15.4999H5.24114C5.40536 15.5004 5.56803 15.4682 5.71975 15.4054C5.87146 15.3425 6.00921 15.2502 6.125 15.1338L15.5 5.75879C15.7341 5.52418 15.8655 5.20631 15.8655 4.87492C15.8655 4.54352 15.7341 4.22566 15.5 3.99106V3.99106ZM13 6.49106L9.50889 2.99992L11.125 1.38379L14.6161 4.87492L13 6.49106Z"
                                        fill="#1E355C"
                                    />
                                </svg>
                            </a>
                        )}
                        <a
                            className={`${styles.actionLink} ${disabledLink}`}
                            onClick={props.shouldDisableAction ? null : () => props.onRemoveMedication?.(medication)}
                        >
                            <svg className={styles.absolute} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L13 13" stroke="#FF2800" strokeWidth="2" />
                            </svg>
                            <svg className={styles.absolute} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 13L13 1" stroke="#FF2800" strokeWidth="2" />
                            </svg>
                        </a>
                    </div>
                </div>
            );
        }
    }

    function renderStatus(pharmacyOrderMedicationStatus: PharmacyOrderMedicationStatus | null): JSX.Element {
        let orderStatus: PharmacyOrderStatus | null = null;

        if (pharmacyOrderMedicationStatus === PharmacyOrderMedicationStatus.Approved) {
            orderStatus = PharmacyOrderStatus.ToPrepare;
        }
        else if (pharmacyOrderMedicationStatus === PharmacyOrderMedicationStatus.OnHold) {
            orderStatus = PharmacyOrderStatus.OnHold;
        }

        return (
            <PharmacyOrdersStatusView
                orderStatus={orderStatus}
            />
        );
    }

    const dragStartHandler = (item: TableDataSourceItem<MedicationModel>): void => {
        setRowBeingDragged(item.Model);
    };

    const dropHandler = (item: TableDataSourceItem<MedicationModel>): void => {
        const draggedRowIndex = props.medicationModel.findIndex(p => p.EMRTemplateSortIndex === rowBeingDragged.EMRTemplateSortIndex);
        const dropTargetRowIndex = props.medicationModel.findIndex(p => p.EMRTemplateSortIndex === item.Model.EMRTemplateSortIndex);

        const updatedItems = [...props.medicationModel];
        const [removed] = updatedItems.splice(draggedRowIndex, 1);
        updatedItems.splice(dropTargetRowIndex, 0, removed);
        const newMedicationModel = updatedItems.map((p, index) => { p.EMRTemplateSortIndexSet(index); return p; });
        props.onDropHandlerCallback?.(newMedicationModel);
        setRowBeingDragged(null);
    };

    return (
        <div id="posref" className="ws-scrollable">
            <MyleTable
                TableRenderOptions={getTableRenderOptions(tableColumnOptions)}
                TableId="prescriptionRenewalsViewRenewMedicationTable"
                Columns={getTableColumns()}
                DataSource={Immutable.List(props.medicationModel.map(p => new TableDataSourceItem<MedicationModel>(p)))}
                CssClass={props.shouldDisableEvent ? "no-hover" : ""}
                isDraggable={!props.shouldDisableAction}
                onDragStart={item => dragStartHandler(item)}
                onDrop={item => dropHandler(item)}
            />
        </div>
    );
}
